import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getRequest, patchRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';
import { getReportType, buildFileUrl } from './reportTypes';

const getAuthHeaders = () => {
  const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const UpdateReport = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const config = getReportType(type);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [current, setCurrent] = useState(null);
  const [formData, setFormData] = useState(config.emptyForm);

  useEffect(() => {
    const fetchItem = async () => {
      setFetching(true);
      try {
        const res = await getRequest(`${config.endpoint}/${id}`, getAuthHeaders());
        if (res?.data?.success) {
          const item = res.data.data;
          setCurrent(item);
          const next = { ...config.emptyForm };
          Object.keys(next).forEach((key) => {
            if (item[key] !== undefined && item[key] !== null) next[key] = item[key];
          });
          next.file = null;
          setFormData(next);
        } else {
          toast.error(res?.data?.message || `Failed to load ${config.label.toLowerCase()}`);
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || `Error fetching ${config.label.toLowerCase()}`);
      } finally {
        setFetching(false);
      }
    };

    if (id) fetchItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, id]);

  const handleChange = (e) => {
    const { name, value, type: inputType, files } = e.target;
    if (inputType === 'file') {
      setFormData((prev) => ({
        ...prev,
        [name]: files && files.length > 0 ? files[0] : null,
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const current = getReportType(type);
    for (const field of current.fields) {
      if (field.required && field.type !== 'file') {
        const val = formData[field.name];
        if (val === undefined || String(val).trim() === '') {
          toast.error(`${field.label} is required`);
          return;
        }
      }
    }

    setLoading(true);

    const payload = new FormData();
    current.fields.forEach((field) => {
      if (field.type === 'file') {
        if (formData[field.name]) payload.append(field.name, formData[field.name]);
      } else if (formData[field.name] !== undefined) {
        payload.append(field.name, formData[field.name]);
      }
    });

    try {
      const response = await patchRequest(`${current.endpoint}/${id}`, payload, getAuthHeaders());
      if (response?.data?.success) {
        toast.success(`${current.label} updated successfully`);
        navigate(`/reports?type=${type}`);
      } else {
        toast.error(response?.data?.message || 'Update failed');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error updating');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <div className="text-center mt-5">Loading {config.label.toLowerCase()}...</div>;

  return (
    <div className="container-fluid mt-5">
      <div className="mainheadig mx-4">
        <h4 className="font-weight-bold">Update {config.label}</h4>
      </div>

      <form onSubmit={handleSubmit} className="form-style">
        <div className="row mx-4 mt-5">
          {config.fields.map((field) => (
            <div className="col-md-6 mt-2" key={field.name}>
              <label htmlFor={field.name} className="lables">
                {field.label} {field.required ? '*' : '(optional)'}
              </label>

              {field.type === 'select' && (
                <select
                  id={field.name}
                  name={field.name}
                  className="form-control"
                  value={formData[field.name] || ''}
                  onChange={handleChange}
                >
                  {field.options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              )}

              {field.type === 'textarea' && (
                <textarea
                  id={field.name}
                  name={field.name}
                  rows="3"
                  className="form-control"
                  value={formData[field.name] || ''}
                  onChange={handleChange}
                />
              )}

              {field.type === 'file' && (
                <>
                  <input
                    id={field.name}
                    name={field.name}
                    type="file"
                    className="form-control-file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.zip,image/*"
                    onChange={handleChange}
                  />
                  {formData[field.name] && (
                    <small className="text-muted d-block mt-1">
                      Selected: {formData[field.name].name}
                    </small>
                  )}
                  {!formData[field.name] && current?.file_path && (
                    <small className="d-block mt-1">
                      <a href={buildFileUrl(current.file_path)} target="_blank" rel="noreferrer">
                        Current file
                      </a>
                      <span className="text-muted"> (leave blank to keep)</span>
                    </small>
                  )}
                </>
              )}

              {!['select', 'textarea', 'file'].includes(field.type) && (
                <input
                  id={field.name}
                  name={field.name}
                  type={field.type || 'text'}
                  className="form-control"
                  value={formData[field.name] ?? ''}
                  onChange={handleChange}
                />
              )}
            </div>
          ))}
        </div>

        <div className="mt-5 text-right mx-4">
          <button type="submit" className="btn btn-lg addbtn" disabled={loading}>
            {loading ? 'Updating...' : `Update ${config.label}`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UpdateReport;
